import { createEffect, createEvent, createStore, sample } from 'effector'
import { createQuery } from 'effector-apollo'

import { SectionPriceType } from '@shared/api/types'

import { toggleFavoritePharmacyMutation } from '@entities/favoritePharmacy'

import client, { cache } from 'src/apollo/client'
import {
  $$drugSearchConfigurationStorage,
  DrugSearchConfiguration,
} from 'src/shared/storage/drugSearch'

import { DRUG_SEARCH_PRICES_QUERY } from '../api/drugSearchPricesQuery'

import { defaultConfig, isDrugRejection, prepareVariables } from './helpers'
import { DrugSearchFlowEntrypoint, RequestData } from './types'

export { DrugSearchFlowEntrypoint } from './types'

export {
  isPbmRetailPharmacy,
  isMailOrderPrice,
  isPbmRetailPrice,
  isRejectedPrice,
} from './helpers'

export const drugDetailsRequested = createEvent<RequestData>()

export const $drugSearchFlowEntrypoint = createStore(
  DrugSearchFlowEntrypoint.Medications,
)

export const evictPricesFx = createEffect(() => {
  cache.evict({ fieldName: 'drugPrices' })
})

export const drugSearchPricesQuery = createQuery({
  document: DRUG_SEARCH_PRICES_QUERY,
  client,
})

export const $drugSearchPrices = drugSearchPricesQuery.$data.map((data) =>
  data?.drugSearchPrices.kind === 'RegularDrugPrices'
    ? data.drugSearchPrices
    : null,
)

export const $bestPriceId = drugSearchPricesQuery.$data.map((data) => {
  return data?.drugSearchPrices.kind === 'RegularDrugPrices'
    ? data.drugSearchPrices.bestPriceId
    : null
})

export const $daysSupply = drugSearchPricesQuery.$data.map((data) =>
  data?.drugSearchPrices.kind === 'RegularDrugPrices'
    ? data.drugSearchPrices.daysSupply
    : null,
)

export const $currentDrugConfig =
  createStore<DrugSearchConfiguration>(defaultConfig)

export const $recommendedPharmacies = $drugSearchPrices.map(
  (data) =>
    data?.prices.filter(
      (price) => price.section === SectionPriceType.Recommended,
    ) ?? [],
)

export const $favoritePharmacies = $drugSearchPrices.map(
  (data) =>
    data?.prices.filter(
      (price) => price.section === SectionPriceType.Favorite,
    ) ?? [],
)

export const $otherPharmacies = $drugSearchPrices.map(
  (data) =>
    data?.prices.filter((price) => price.section === SectionPriceType.Other) ??
    [],
)

export const $specialtyPharmacies = $drugSearchPrices.map(
  (data) =>
    data?.prices.filter(
      (price) => price.section === SectionPriceType.Specialty,
    ) ?? [],
)

export const $rejectionInfo = drugSearchPricesQuery.$data.map((data) =>
  data?.drugSearchPrices.kind === 'DrugRejection'
    ? data.drugSearchPrices
    : null,
)

export const $hasErrorInPricesRequest = createStore(false)

sample({
  clock: drugDetailsRequested,
  source: $$drugSearchConfigurationStorage.$state,
  filter: (savedDrugs, requestData) =>
    savedDrugs.some(({ drugName }) => drugName === requestData.drugName),
  fn: prepareVariables,
  target: drugSearchPricesQuery.refresh,
})

sample({
  clock: drugSearchPricesQuery.finished.success,
  filter: ({ data }) => isDrugRejection(data),
  fn: () => true,
  target: $hasErrorInPricesRequest,
})

sample({
  source: drugSearchPricesQuery.$error,
  fn: Boolean,
  target: $hasErrorInPricesRequest,
})

sample({
  clock: toggleFavoritePharmacyMutation.start,
  target: evictPricesFx,
})
